import React, { useCallback, useMemo } from 'react';

import { Alert, Button, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import QuickViewDefault from 'v2/components/Shared/QuickView';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateFormat } from 'v2/formatters/date';
import { InvoiceStatus, SubscriptionStatus, WebhookStatus } from 'v2/interfaces/webhook2';
import type { Webhook, WebhookHistory } from 'v2/interfaces/webhook2';

import { Icon } from '@eduzz/ui-antd-theme';

import { HistoryStatus } from './FilterWebhookHistory';
import { ALL_PRODUCTS_ID } from '../WebhookForm/Fields';

type HistoricItem = NonNullable<WebhookHistory['sends']>[number];

const HTTP_STATUS_STYLE: Record<string, string> = {
  'status-success': 'bg-(--uizz-antd-color-success) text-(--uizz-antd-color-text-light-solid)',
  'status-failed': 'bg-(--uizz-antd-color-error) text-(--uizz-antd-color-text-light-solid)'
};

type IProps = {
  webhook: WebhookHistory;
  open: boolean;
  handleClose: () => void;
  url: Webhook;
};

export const QuickView = ({ webhook, open, handleClose, url }: IProps) => {
  const solveStatusColor = useCallback((status: string): string => {
    if (!status) return 'status-pending';

    const statusInt = Number.parseInt(status);

    if (statusInt >= 200 && statusInt < 300) {
      return 'status-success';
    }

    return 'status-failed';
  }, []);

  const historic = webhook?.sends || [];
  const sent = webhook?.sentData ? JSON.stringify(JSON.parse(webhook.sentData), undefined, 2) : '';

  const [sentData, setSentData] = React.useState<string>();

  const requestCount = parseInt(String(webhook?.count ?? 0));
  const address = webhook?.lastSend?.url || url?.url;

  const handleSentDataCopy = (e: React.SyntheticEvent, w: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(w);
    setSentData(w);

    setTimeout(() => {
      setSentData(null);
    }, 2000);
  };

  const columns = useMemo(
    (): ColumnsType<HistoricItem> => [
      {
        title: 'Code',
        key: 'code',
        render: (_, item) => (
          <span className={`px-1.5 py-0.75 ${HTTP_STATUS_STYLE[solveStatusColor(item.returnStatus)] || ''}`}>
            {item.returnStatus}
          </span>
        )
      },
      {
        title: 'Resposta',
        key: 'response',
        render: (_, item) => (
          <Truncate count={20} final={'...'}>
            {item?.returnData || '-'}
          </Truncate>
        )
      },
      {
        title: 'Data',
        key: 'date',
        render: (_, item) => dateFormat(item.created_at, 'dd/MM/yyyy, HH:mm:ss')
      }
    ],
    [solveStatusColor]
  );

  return (
    <QuickViewDefault title='Detalhes do Webhook' open={open} onClose={handleClose}>
      <div className='flex flex-col gap-3'>
        <div>
          <Typography.Text strong>URL</Typography.Text>
          <Typography.Text className='block'>{address}</Typography.Text>
        </div>
        <div>
          <Truncate count={50} final='...' title={webhook?.product.title}>
            <Typography.Text strong>Produto</Typography.Text>
            <Typography.Text className='block'>
              {webhook?.product.id !== ALL_PRODUCTS_ID && `(${webhook?.product.id}) ${webhook?.product.title}`}
            </Typography.Text>
          </Truncate>
        </div>
        <div>
          <Truncate count={25} final='...' title={webhook?.customer.name}>
            <Typography.Text strong>Comprador</Typography.Text>
            <Typography.Text className='block'>{webhook?.customer.name}</Typography.Text>
          </Truncate>
        </div>

        {webhook?.type === 'invoice' && (
          <div>
            <Typography.Text strong>Fatura</Typography.Text>
            <Typography.Text className='block'>
              {webhook?.entityId} - {InvoiceStatus[webhook.entityStatus as keyof typeof InvoiceStatus] ?? ''}
            </Typography.Text>
          </div>
        )}

        {webhook?.type === 'contract' && (
          <div>
            <Typography.Text strong>Contrato</Typography.Text>
            <Typography.Text className='block'>
              {webhook?.entityId} - {SubscriptionStatus[webhook?.entityStatus as keyof typeof SubscriptionStatus]}
            </Typography.Text>
          </div>
        )}

        {requestCount > 0 && (
          <Typography.Text>
            <Typography.Text strong>Tentativas:&nbsp;</Typography.Text>
            {requestCount}
          </Typography.Text>
        )}

        {url?.state === WebhookStatus.failing && webhook?.status === HistoryStatus.PENDING && (
          <Alert
            type='warning'
            className='text-sm'
            message={
              <>
                A sua url está <Typography.Text strong>falhando</Typography.Text>, para que este evento seja enviado
                para a sua URL{' '}
                <Typography.Text strong>(além de todos os outros eventos pendentes no histórico)</Typography.Text> é
                necessário corrigir os erros com a sua integração.
              </>
            }
          />
        )}

        {webhook?.status === HistoryStatus.ARCHIVED && (
          <Alert
            type='warning'
            className='text-sm'
            message='Este evento foi arquivado, para reenviar, basta utilizar o botão de ação de retentativa.'
          />
        )}

        {webhook?.status === HistoryStatus.FAILED && (
          <Alert
            type='error'
            className='text-sm'
            message={
              <>
                Ao enviar esse evento para sua URL, ela retornou o status HTTP{' '}
                <Typography.Text strong>{webhook?.lastSend?.returnStatus}</Typography.Text>. Por favor, corrija os erros
                para que na próxima tentativa o evento seja recebido com sucesso.
              </>
            }
          />
        )}

        <div>
          <Typography.Text strong className='mb-2 block text-base'>
            Histórico de envios
          </Typography.Text>
          {historic.length === 0 ? (
            <Typography.Text type='secondary' className='block w-full py-6 text-center text-sm'>
              Nenhum histórico encontrado.
            </Typography.Text>
          ) : (
            <Table
              size='small'
              columns={columns}
              dataSource={historic}
              rowKey='_id'
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          )}
        </div>

        <div>
          <div className='mb-2 flex items-center gap-2'>
            <Typography.Text strong className='text-base'>
              Dados enviados
            </Typography.Text>
            <Tooltip title={sent === sentData ? 'Copiado!' : 'Clique para copiar'}>
              <Button
                type='text'
                shape='circle'
                aria-label='Copiar dados enviados'
                icon={<Icon.Copy size={16} />}
                onClick={e => handleSentDataCopy(e, sent)}
              />
            </Tooltip>
          </div>
          <pre className='m-0 w-full overflow-auto rounded-sm bg-(--uizz-antd-color-fill-quaternary) p-2 text-xs'>
            {sent}
          </pre>
        </div>
      </div>
    </QuickViewDefault>
  );
};
